import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";

import type { StaleVersionError } from "@/api";
import type { components } from "@k60/contracts";
import { Button } from "@/components/Button";
import { ConfirmReleaseLoss } from "@/components/ConfirmReleaseLoss";
import { LengthDoubt } from "@/components/LengthDoubt";
import { AlertIcon } from "@/components/icons";
import { FACADE_CANCELLED, FACADE_SENT, type FacadeEditability } from "@/lib/facadeEditability";
import { formatMetres, formatMm } from "@/lib/format";
import { LENGTH_REFUSALS, parseLengthMm, wallLengthDoubt } from "@/lib/length";
import { carriesMeasure, measureWord } from "@/lib/outline";

import { useMeasureWrite, type LostMeasure } from "./measureWrite";
import styles from "./WallMeasureField.module.css";

export type { LostMeasure } from "./measureWrite";

type PlanningStateWire = components["schemas"]["PlanningStateIn"];

/** One of the two gestures this field writes. */
type Gesture = { kind: "set"; mm: number } | { kind: "remove" };

interface WallMeasureFieldProps {
  caseId: string;
  /** The document's own id (`w1`, …) — what both ops are keyed by. */
  wallId: string;
  /** „Wand 3“ — how the screen names this wall: in the question before a
   * released Vorgang loses its release, and in the Stand's change summary. */
  wallName: string;
  /** The server's figure, `null` where the wall carries none. On a wall a
   * chamfer shortened, the customer's measure up to the corner point (design
   * 2026-10-18 §5.1) — what was stated, and what this field changes. */
  lengthMm: number | null;
  /**
   * On a wall a chamfer shortened, both its lengths in the board's own words
   * — „2.500 mm belegbar (3.000 bis zum Eckpunkt)“ — so the stretch the
   * gratings lie on stands beside the figure that is typed; `null`
   * elsewhere.
   */
  usable?: string | null;
  /** `SegmentOut.lengthSource`; absent means `read`, as everywhere. */
  lengthSource: string | undefined;
  /** What was read before a person typed over it (E-46), or nothing. */
  displacedReadingMm?: number | null;
  /**
   * The stated figure the sketch contradicts (E-64), on a wall that carries
   * no length until a person decides — said in place of „ohne Maß“, the way
   * the board writes it beside the wall. Never put into the field: the field
   * is where a new figure is typed.
   */
  disputedMm?: number | null;
  /** The lock this gesture posts against, unless {@link settle} says another. */
  expectedVersionNo: number;
  /** The board as it stands — a Stand is the whole board, never a diff. */
  planningState: PlanningStateWire;
  editability: FacadeEditability;
  /** The release a gesture on a `quote_approved` Vorgang would end. `null`
   * while it is not loaded — the question is asked all the same. */
  approval: { by: string | null; at: string } | null;
  /** The ring, in one sentence (`closureSentence`), under the field. */
  closure?: string | null;
  /** How far this wall's axis is short (`axisGap`) — **shown, never put into
   * the field** (spec §1, E-13). */
  axisGapMm?: number | null;
  /**
   * Writes the board still holds go first, and the number to post against
   * comes back — or `null` where a reload is on its way and nothing may be
   * written. Without it the field posts against {@link expectedVersionNo}.
   */
  settle?: () => Promise<number | null>;
  /** The new Stand is written; the field asks for the plan again itself. */
  onSaved: (versionNo: number) => void;
  /**
   * The gesture is on the wire (`true`), and has come back (`false`) — after
   * {@link onSaved} or {@link onStale}, or after a refusal or failure that
   * wrote nothing. For the board's save state in its head (Welle 1, idea 23).
   */
  onWriting?: (writing: boolean) => void;
  /** Somebody else's Stand landed first — the board's own 409 handling, told what did not land. */
  onStale: (cause: StaleVersionError, lost: LostMeasure) => void;
}

/** „40 mm“, „1.250 mm“ — the figures in a sentence the server's sums wrote. */
const FIGURE = /(\d[\d.]*(?:,\d+)? mm)/;

/**
 * A sentence with every millimetre figure in the mono face (CI §4) — the
 * ring's sentence is built as text (`closureSentence`), so the figures are
 * found in it again rather than handed over twice.
 */
function withFigures(sentence: string) {
  return sentence.split(FIGURE).map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="num">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

/** What the field starts from: the wall's own figure, or nothing. Never a
 * remainder, never a drawn distance. */
function startText(lengthMm: number | null, lengthSource: string | undefined): string {
  return carriesMeasure({ lengthMm, lengthSource }) ? String(lengthMm) : "";
}

/**
 * „Maß“ on the board (spec 2026-10-15 §2): type a wall's length, or take it
 * off, on a Vorgang that already has a Stand — and see at once what the ring
 * makes of it.
 *
 * **Not optimistic.** A length moves the whole drawing, and only the server
 * knows where to (`settle()`): the gesture is posted, the plan read again,
 * and until then the field is locked and says „Wird neu gezeichnet …“. A
 * figure shown before the server drew it would be a figure nobody drew.
 *
 * **Not on the undo stack** (F1-3). Rückgängig would type the old figure back
 * as a new one — and a read figure typed back is „von dir“ ever after. The
 * hint under the field says so; what was read stays visible beside a typed
 * figure instead.
 */
export function WallMeasureField({
  caseId,
  wallId,
  wallName,
  lengthMm,
  usable = null,
  lengthSource,
  displacedReadingMm = null,
  disputedMm = null,
  expectedVersionNo,
  planningState,
  editability,
  approval,
  closure = null,
  axisGapMm = null,
  settle,
  onSaved,
  onWriting,
  onStale,
}: WallMeasureFieldProps) {
  const writeMeasure = useMeasureWrite({
    caseId,
    wallId,
    wallName,
    expectedVersionNo,
    planningState,
    settle,
    onSaved,
    onWriting,
    onStale,
  });
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(() => startText(lengthMm, lengthSource));
  // The server's figure the text was last set from: a new one (after this
  // field's own gesture, or a reload) puts the field back on it — unless
  // somebody is typing, which is only ever between two such figures.
  const figure = `${lengthMm ?? ""}:${lengthSource ?? ""}`;
  const [shownFigure, setShownFigure] = useState(figure);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState<Gesture | null>(null);
  /**
   * A typed figure longer than any facade (`wallLengthDoubt`, UX-Gutachten
   * 23.10.2026 Nr. 7) and the one offered instead — asked inline, before
   * anything is written; the question before a release goes comes after it.
   */
  const [doubt, setDoubt] = useState<{ mm: number; suggestionMm: number } | null>(null);
  /** At the field: what was typed is no measure, or the server refused the value. */
  const [error, setError] = useState<string | null>(null);
  /** The live line: done, unchanged, or why the write did not land. */
  const [outcome, setOutcome] = useState<{ text: string; refused: boolean } | null>(null);
  const [cancelledHere, setCancelledHere] = useState(false);
  /**
   * Back into the field once it is unlocked. A locked field cannot keep the
   * focus it had, and focusing it the moment the write returns is a render
   * too early — it is still `disabled` then, and the focus fell to the page
   * (seen in the browser, 15.10.2026).
   */
  const refocusRef = useRef(false);
  useEffect(() => {
    if (busy || !refocusRef.current) return;
    refocusRef.current = false;
    inputRef.current?.focus();
  }, [busy]);

  if (figure !== shownFigure && !busy) {
    setShownFigure(figure);
    setText(startText(lengthMm, lengthSource));
  }

  const measured = carriesMeasure({ lengthMm, lengthSource });
  const word =
    !measured && disputedMm !== null
      ? `${formatMetres(disputedMm)} — passt nicht zur Skizze`
      : measureWord({ lengthMm, lengthSource });
  const displaced =
    word === "von dir" && displacedReadingMm !== null && displacedReadingMm !== lengthMm
      ? displacedReadingMm
      : null;

  const stateLine = (
    <span className={styles.state} id={`${id}-state`} data-testid="measure-state">
      {word}
      {displaced !== null ? (
        <>
          {" · gelesen "}
          <span className="num">{formatMm(displaced)}</span>
        </>
      ) : null}
    </span>
  );

  const usableLine =
    usable !== null ? (
      <p id={`${id}-usable`} className={`num ${styles.usable}`} data-role="usable">
        {usable}
      </p>
    ) : null;

  const closureLine =
    closure !== null ? (
      <p className={styles.closure} data-role="closure">
        {withFigures(closure)}
      </p>
    ) : null;

  const frozen = cancelledHere ? "cancelled" : editability;
  if (frozen === "sent" || frozen === "cancelled") {
    return (
      <div className={styles.field} data-role="wall-measure">
        <div className={styles.head}>
          <span className={`label ${styles.label}`}>Maß</span>
          {stateLine}
        </div>
        {measured ? <span className={`num ${styles.value}`}>{formatMm(lengthMm)}</span> : null}
        {usableLine}
        <p className={styles.frozen}>{frozen === "sent" ? FACADE_SENT : FACADE_CANCELLED}</p>
        {closureLine}
      </div>
    );
  }

  async function write(gesture: Gesture): Promise<void> {
    setBusy(true);
    setError(null);
    setOutcome(null);
    const outcome = await writeMeasure(gesture);
    switch (outcome.kind) {
      case "saved":
        setOutcome({ text: gesture.kind === "set" ? "Maß gesetzt." : "Maß entfernt.", refused: false });
        break;
      case "cancelled":
        setCancelledHere(true);
        break;
      case "invalid":
        setError(outcome.message);
        break;
      case "refused":
        setOutcome({ text: outcome.message, refused: true });
        break;
      case "held":
      case "stale":
        break;
    }
    // Back into the field — except where a reload is on its way and the
    // board says why (`held`).
    refocusRef.current = outcome.kind !== "held";
    setBusy(false);
  }

  function request(gesture: Gesture): void {
    if (busy) return;
    if (editability === "approved") {
      setAsking(gesture);
      return;
    }
    void write(gesture);
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (busy) return;
    const parsed = parseLengthMm(text);
    if (!parsed.ok) {
      setOutcome(null);
      setError(LENGTH_REFUSALS[parsed.reason === "empty" ? "unreadable" : parsed.reason]);
      return;
    }
    if (measured && parsed.mm === lengthMm) {
      // The same figure is no gesture — and a read figure typed again would
      // come back „von dir“, a change of who stated it that nobody made.
      setError(null);
      setOutcome({ text: "Maß unverändert.", refused: false });
      return;
    }
    const suggestionMm = wallLengthDoubt(parsed.mm);
    if (suggestionMm !== null) {
      setError(null);
      setOutcome(null);
      setDoubt({ mm: parsed.mm, suggestionMm });
      return;
    }
    request({ kind: "set", mm: parsed.mm });
  }

  /**
   * The answer to the doubt: the figure offered, or the one typed. The focus
   * goes back to the field first — the buttons go with the question, and a
   * release question opened from here hands the focus back to whatever had
   * it when it opened.
   */
  function answerDoubt(mm: number): void {
    setDoubt(null);
    setText(String(mm));
    inputRef.current?.focus();
    if (measured && mm === lengthMm) {
      // The figure offered is the wall's own (review of d8516611, M-1): no
      // gesture, as for the same figure typed — a read figure stays read.
      setOutcome({ text: "Maß unverändert.", refused: false });
      return;
    }
    request({ kind: "set", mm });
  }

  /** Escape in the question: back into the field, the figure as typed — nothing was written. */
  function dismissDoubt(): void {
    setDoubt(null);
    inputRef.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== "Escape") return;
    // The field's own Escape: back to the server's figure. The board's
    // Escape (let go of the wall) leaves inputs alone.
    event.preventDefault();
    setText(startText(lengthMm, lengthSource));
    setError(null);
    // The question was about the figure this takes back (M-6).
    setDoubt(null);
  }

  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const gapId = `${id}-gap`;
  const doubtId = `${id}-doubt`;
  const showGap = axisGapMm !== null && axisGapMm > 0;
  const describedBy = [
    error ? errorId : null,
    doubt !== null ? doubtId : null,
    `${id}-state`,
    usable !== null ? `${id}-usable` : null,
    hintId,
    showGap ? gapId : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.field} data-role="wall-measure">
      <form className={styles.form} noValidate onSubmit={submit}>
        <div className={styles.head}>
          <label className={`label ${styles.label}`} htmlFor={`${id}-input`}>
            Maß
          </label>
          {stateLine}
        </div>
        <input
          ref={inputRef}
          id={`${id}-input`}
          className={`num ${styles.input} ${error ? styles.inputBad : ""}`}
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          value={text}
          disabled={busy}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            setText(event.target.value);
            setError(null);
            setOutcome(null);
            setDoubt(null);
          }}
          onKeyDown={onKeyDown}
        />
        {error ? (
          <span id={errorId} className={styles.error}>
            {error}
          </span>
        ) : null}
        {doubt !== null ? (
          <LengthDoubt
            questionId={doubtId}
            mm={doubt.mm}
            suggestionMm={doubt.suggestionMm}
            onAnswer={answerDoubt}
            onDismiss={dismissDoubt}
          />
        ) : null}
        {usableLine}
        <span id={hintId} className={styles.hint}>
          mm oder Meter mit Komma — 5120 oder 5,12. Rückgängig nimmt ein gesetztes Maß nicht
          zurück.
        </span>
        <div className={styles.actions}>
          <Button type="submit" disabled={busy}>
            Maß setzen
          </Button>
          {measured ? (
            <Button variant="leise" disabled={busy} onClick={() => request({ kind: "remove" })}>
              Maß entfernen
            </Button>
          ) : null}
        </div>
      </form>

      {/* One region for every answer, mounted before the first one: a
          region that arrives already full is not reliably read out. */}
      <p
        role="status"
        className={`${styles.outcome} ${outcome?.refused ? styles.outcomeRefused : ""}`}
        data-role="measure-outcome"
      >
        {busy ? (
          "Wird neu gezeichnet …"
        ) : outcome ? (
          <>
            {outcome.refused ? <AlertIcon size={14} className={styles.outcomeIcon} /> : null}
            {outcome.text}
            {/* The ring's answer to it, for whoever cannot see the line
                below change. */}
            {!outcome.refused && closure !== null ? (
              <span className="visually-hidden"> {closure}</span>
            ) : null}
          </>
        ) : null}
      </p>

      {closureLine}
      {showGap ? (
        <p id={gapId} className={styles.gap} data-role="axis-gap">
          Auf dieser Achse fehlen <span className="num">{formatMm(axisGapMm)}</span>.
        </p>
      ) : null}

      {asking !== null ? (
        <ConfirmReleaseLoss
          by={approval?.by ?? null}
          at={approval?.at ?? null}
          subtitle={
            asking.kind === "set" ? (
              <>
                {wallName}: Maß auf <span className="num">{formatMm(asking.mm)}</span>
              </>
            ) : (
              `${wallName}: Maß entfernen`
            )
          }
          onCancel={() => setAsking(null)}
          onConfirm={() => {
            const gesture = asking;
            setAsking(null);
            void write(gesture);
          }}
        />
      ) : null}
    </div>
  );
}
